import {
  ChangeDetectionStrategy,
  AfterViewInit,
  Component,
  DestroyRef,
  ElementRef,
  inject,
  input,
  model,
  OnDestroy,
  output,
  signal,
  computed,
  effect,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subject, debounceTime } from 'rxjs';
import { SearchMinLength } from '../../../core/common/enums/search-min-length';
import { AvatarComponent } from '../avatar/avatar.component';
import { LoaderComponent } from '../loader/loader.component';
import { TooltipDirective } from '../tooltip/tooltip.directive';
import { TrimTooltipDirective } from '../../directives/trim-tooltip/trim-tooltip.directive';
import { TranslocoPipe } from '@jsverse/transloco';
import { BadgeComponent } from '../badge/badge.component';
import {
  SearchIconComponent,
  ChevronDownIconComponent,
  RemoveItemIconComponent,
  VariableIconComponent,
  AddIconComponent,
} from '@kuiper/icons';
import tippy, { Instance, Props, Placement } from 'tippy.js';
import { Option } from '@kuiper/ibusiness-core';

@Component({
  selector: 'lib-user-dropdown-view',
  imports: [
    AvatarComponent,
    BadgeComponent,
    TooltipDirective,
    LoaderComponent,
    TrimTooltipDirective,
    TranslocoPipe,
    SearchIconComponent,
    ChevronDownIconComponent,
    RemoveItemIconComponent,
    VariableIconComponent,
    AddIconComponent,
  ],
  templateUrl: './user-dropdown-view.component.html',
  styleUrl: './user-dropdown-view.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class UserDropdownViewComponent implements AfterViewInit, OnDestroy {
  userTableList = input.required<any[]>();
  userCount = input.required<number>();
  containerRef = input.required<string>();

  userDropdownList = model<any[]>([]);
  isDropdownLoading = input<boolean>(false);
  currentDropdownPage = input<number>(1);
  totalDropdownPages = input<number>(20);
  totalDropdownCount = model<number>(0);
  assigneeType = input<string[]>([]);
  toolTipClass = input<string>('');
  croppedNumber = input<number>(3);
  showIndividualAvatar = input<boolean>(true);
  trimLength = input<number>(22);
  showAssigneeName = input<boolean>(false);

  enableLocalSearch = input<boolean>(false);
  searchLabel = input<string>('Find Users');
  searchPlaceholder = input<string>('Search by name'); //to be able to translate or edit it
  noSearchResultLabel = input<string>('No search result'); //to be able to translate or edit it
  headerListLabel = input<string>('Owners'); //to be able to translate or edit it

  menuHeight = input<number>(0);
  imgDimensions = input<{ width: number; height: number }>({
    width: 2,
    height: 2,
  });

  enableDropdownIcon = input<boolean>(true);
  showVariableIcon = input<boolean>(false);
  allowLeftPositioning = input<boolean>(false);
  toggleContainerClass = input<string>('justify-between w-[10.5rem]');

  enableDeleteItem = input<boolean>(false);
  disableDeleteItem = input<boolean>(false);
  deleteItem = output<number>();

  enableAddItem = input<boolean>(false);
  disableAddItem = input<boolean>(false);
  addItem = output<Option>();

  croppedArray = computed(() => {
    if (this.userTableList().length > this.croppedNumber()) {
      return this.userTableList().slice(0, this.croppedNumber());
    } else {
      return this.userTableList();
    }
  });
  isMenuOpen = signal(false);
  openedMenu = output<boolean>();
  handleMenuToggle = output<{ currentPage: number; search: string }>();
  allowCustomToggleButton = input<boolean>(false);

  isMenuOpenEffect = effect(() => {
    const isOpen = this.isMenuOpen();
    this.openedMenu.emit(isOpen);
  });

  searchTerm = signal<string>('');
  searchTerm$ = new Subject<string>();
  isMoreThanThreeCharacters = signal<boolean>(false);
  minSearchLength = signal<number>(SearchMinLength.length);
  private readonly destroyRef = inject(DestroyRef);

  private tippyMenuInstance: Instance<Props> | null = null;
  private currentTippyTrigger: HTMLElement | null = null;
  private menuShouldOpen = false;
  menuOverlayContent =
    viewChild<ElementRef<HTMLDivElement>>('menuOverlayContent');
  searchInput = viewChild<ElementRef<HTMLInputElement>>('searchInput');

  ngAfterViewInit() {
    this.searchTerm$
      .pipe(debounceTime(600), takeUntilDestroyed(this.destroyRef))
      .subscribe(term => {
        this.onDropdownSearch(term);
      });
  }

  toggleMenu(event: MouseEvent) {
    if (
      (this.croppedArray().length === 1 && this.showIndividualAvatar()) ||
      ((this.croppedArray().length > 1 || !this.showIndividualAvatar()) &&
        !this.enableDropdownIcon())
    ) {
      return;
    }
    const triggerElement = event.currentTarget as HTMLElement | null;
    if (!triggerElement) {
      return;
    }

    this.ensureTippyMenu(triggerElement);

    if (!this.tippyMenuInstance) {
      return;
    }

    this.menuShouldOpen = !this.isMenuOpen();

    if (this.menuShouldOpen) {
      const param = { currentPage: 1, search: '' };
      this.handleMenuToggle.emit(param);
      this.tippyMenuInstance.show();
    } else {
      this.tippyMenuInstance.hide();
    }
  }

  private ensureTippyMenu(triggerElement: HTMLElement) {
    const content = this.menuOverlayContent();

    if (!content) {
      return;
    }

    if (
      this.tippyMenuInstance &&
      this.currentTippyTrigger &&
      this.currentTippyTrigger !== triggerElement
    ) {
      this.tippyMenuInstance.destroy();
      this.tippyMenuInstance = null;
    }

    if (this.tippyMenuInstance) {
      return;
    }

    content.nativeElement.classList.remove('hidden');

    this.currentTippyTrigger = triggerElement;
    const preferredPlacement: Placement = 'bottom-start';
    const fallbackPlacement: Placement = 'top-end';

    this.tippyMenuInstance = tippy(triggerElement, {
      content: content.nativeElement,
      interactive: true,
      trigger: 'manual',
      hideOnClick: true,
      appendTo: () => document.body,
      zIndex: 9999,
      placement: preferredPlacement,
      popperOptions: {
        strategy: 'fixed',
        modifiers: [
          {
            name: 'offset',
            options: {
              offset: [0, 8],
            },
          },
          {
            name: 'flip',
            options: {
              fallbackPlacements: [fallbackPlacement],
              padding: 8,
            },
          },
          {
            name: 'preventOverflow',
            options: {
              boundary: 'viewport',
              rootBoundary: 'viewport',
              padding: 8,
              altAxis: true,
            },
          },
        ],
      },
      onShow: () => {
        this.isMenuOpen.set(true);
      },
      onHidden: () => {
        this.isMenuOpen.set(false);
        this.resetSearchState();
      },
      onClickOutside: () => {
        this.tippyMenuInstance?.hide();
      },
      onMount: instance => {
        const tippyBox = instance.popper.querySelector(
          '.tippy-box'
        ) as HTMLElement | null;
        const tippyContent = instance.popper.querySelector(
          '.tippy-content'
        ) as HTMLElement | null;

        if (tippyBox) {
          tippyBox.dataset['menuTippy'] = 'true';
          tippyBox.style.overflow = 'visible';
        }

        if (tippyContent) {
          tippyContent.style.overflow = 'visible';
        }
      },
    });
  }

  private resetSearchState() {
    this.searchTerm.set('');
    this.isMoreThanThreeCharacters.set(false);
    const searchInputElement = this.searchInput()?.nativeElement;
    if (searchInputElement) {
      searchInputElement.value = '';
    }
  }

  onSearchTermChange(target: EventTarget | null) {
    const term = (target as HTMLInputElement).value;
    this.searchTerm$.next(term);
  }

  onDropdownSearch(term: string) {
    if (term.length >= 3) {
      this.isMoreThanThreeCharacters.set(true);
      const param = { currentPage: 1, search: term };
      this.searchTerm.set(term);

      if (this.enableLocalSearch()) {
        const isSearchResult = this.userTableList().filter(user =>
          user.name.toLowerCase().includes(term.toLowerCase())
        );
        this.userDropdownList.set(isSearchResult);
        this.totalDropdownCount.set(this.userDropdownList().length);
      } else {
        this.handleMenuToggle.emit(param);
      }
    } else if (
      this.isMoreThanThreeCharacters() &&
      term.length < this.minSearchLength()
    ) {
      this.isMoreThanThreeCharacters.set(false);
      const param = { currentPage: 1, search: '' };
      this.searchTerm.set('');

      if (this.enableLocalSearch()) {
        this.userDropdownList.set(this.userTableList());
        this.totalDropdownCount.set(this.userDropdownList().length);
      } else {
        this.handleMenuToggle.emit(param);
      }
    }
  }

  onScroll(event: Event) {
    const { scrollTop, scrollHeight, clientHeight } =
      event.target as unknown as {
        scrollTop: number;
        scrollHeight: number;
        clientHeight: number;
      };
    if (
      scrollTop + clientHeight >= scrollHeight - 200 &&
      !this.isDropdownLoading() &&
      this.currentDropdownPage() < this.totalDropdownPages()
    ) {
      const param = {
        currentPage: this.currentDropdownPage() + 1,
        search: this.searchTerm(),
      };
      this.handleMenuToggle.emit(param);
    }
  }

  ngOnDestroy(): void {
    this.tippyMenuInstance?.destroy();
  }
}
